/* KANON `Detal klienta v3`, variant a — the three data tabs: Trendy, Pomiary, Rekordy.
 *
 * One stylesheet for three tabs because they are one visual language and the alternative is three
 * near-identical copies of every card, section heading and figure rule — the duplication this
 * repo's styling rule exists to prevent. What differs between the tabs is which GRID a section
 * uses, and those are three rules near the top.
 *
 * ── The v3 argument, in one line ──────────────────────────────────────────────────────────────
 * Hierarchy is carried by SECTION HEADINGS, not by another border around every group. A section is
 * a 17px heading with an aside at the far end, and the cards under it sit directly on the canvas
 * at a 26px section gap.
 *
 * ── The standing contrast deviation ───────────────────────────────────────────────────────────
 * Every eyebrow, aside and caption is `--text-2` where the canon writes `--text-3`. `--text-3`
 * measures 2.17–2.80 against these surfaces and the canon's own tertiary is a light-theme value
 * used on a light-theme mock. Recorded in `docs/design/kanon-clients-2026-08-11/README.md`; kept
 * by the 31.08 decision, which exempts a departure carrying a measured contrast number. */

.tab {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-26px);
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

/* ── the card, and the three grids ─────────────────────────────────────────── */

.card {
  display: flex;
  flex-direction: column;
  gap: var(--space-14px);
  min-width: 0;
  padding: var(--space-15px) var(--space-4);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

/* Two equal tracks with a zero minimum. An explicit width on a flex item caps its MINIMUM and
 * nowrap content then overflows into the neighbour — a failure this app has paid for once. */
.gridSplit {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-5);
  align-items: start;
}

/* ── The three trend charts ───────────────────────────────────────────────────────────────────
 * THREE equal tracks since 2 September 2026, when energy joined tonnage and sleep. The canon draws
 * two; the owner's instruction is that the three "leżą na wspólnej osi czasu", and one row of equal
 * columns is what makes that readable — the same date stands at the same x in all three, so the
 * shared crosshair is a vertical line the eye can follow across the row rather than three marks at
 * three offsets. Stacked, or two-plus-one, the comparison the tab exists for becomes a scroll.
 *
 * `align-items` is NOT `start` here, unlike `.gridSplit`: the tonnage card carries no scale switch
 * and would otherwise end 30px above its neighbours. Stretching evens the three card boxes and
 * leaves the extra space inside the short one, under its caption, where it is invisible. */
.chartGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-5);
  align-items: stretch;
}

/* `min-height` is the RESERVE, not a size: the row is empty until `/records` lands and would
 * otherwise be zero pixels tall. See the note at the call site in `RecordsTab`. */
.big3Grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-5);
  align-items: start;
  min-height: var(--h-lift-card);
}

.colStack {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  min-width: 0;
}

.cardHead {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.aside,
.caption {
  flex: none;
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
}

.caption {
  overflow: hidden;
  text-overflow: ellipsis;
}

.notice {
  margin: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.hint {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

.error {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--danger-text-strong);
}

/* Geometry only — the fill comes from `shared/ui/inkButton`. */
.primary {
  height: var(--h-detail-btn);
  padding: 0 var(--space-14px);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: normal;
  border-radius: var(--radius-mark);
}

/* GEOMETRY ONLY. The treatment is `shared/ui/ghostButton.module.css`, worn as a second class, and
 * nothing may be declared on both sides — see that file. */
.ghost {
  /* The gap between a drawn glyph and the word beside it — the „Pomiar" trigger's plus. Geometry,
   * so it belongs on this side of `ghostButton`'s split. */
  gap: var(--space-6px);
  height: var(--h-detail-btn);
  padding: 0 var(--space-14px);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  background: transparent;
  border-radius: var(--radius-mark);
}

/* ── Trendy: the range bar ─────────────────────────────────────────────────── */

.rangeBar {
  display: flex;
  flex: none;
  gap: var(--space-10px);
  align-items: center;
  padding: var(--space-11px) var(--space-14px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

/* The canon draws a chip printing `11 lipca 2026`. This has to be OPERABLE, so it is a real date
 * input wearing that chip's geometry — the browser prints its own numeric date and its own picker
 * glyph, which is the honest cost of a control the trainer can actually use. */
.rangeInput {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: var(--h-range-field);
  padding: 0 var(--space-11px);
  font-family: var(--font-body);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-8px);
  font-variant-numeric: tabular-nums;
}

.rangeInput:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.rangeArrow {
  flex: none;
  font-size: var(--fs-label);
  line-height: var(--lh-auto);
  color: var(--text-3);
}

.rangeNote {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-2);
  text-align: end;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rangeRefusal {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--danger-text-strong);
}

/* ── Trendy: frekwencja ────────────────────────────────────────────────────── */

.attRow {
  display: flex;
  gap: var(--space-22px);
  align-items: center;
  min-width: 0;
}

/* ── one ring, on the section it is about ────────────────────────────────────
 *
 * There were two, concentric, with their words in a legend beside them, and the owner's reading of
 * that drawing on 1 September 2026 was "ja nie wiem co ona przedstawia aktualnie zupełnie". So the
 * pair is split: this block is instantiated twice, once on the Frekwencja card and once at the head
 * of the muscle-volume figures, and each carries its own name above the arc, the percentage inside
 * it and its denominator in words underneath. Everything a reader needs is inside the block; nothing
 * is left to a legend that has to be matched back to an arc by colour.
 *
 * The name is a SIBLING of the ring rather than a child. `ReadoutRing` is a `role="img"`, which
 * swallows its whole subtree — a label inside it would be unreachable, which is the same trap the
 * strip's legend was pulled out of. */
.ringBlock {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-2);
}

.ringEyebrow {
  font-size: var(--fs-micro-label);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-cat);
  text-transform: uppercase;
  color: var(--text-2);
}

.ringRow {
  display: flex;
  gap: var(--space-4);
  align-items: center;
}

.ringColumn {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-6px);
  align-items: center;
}

/* The denominator the percentage came from. A ring draws a fraction and prints only its result;
 * 69% of sixteen days and 69% of three are the same picture and not the same reading. */
.ringDenominator {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  white-space: nowrap;
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.counters {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  margin: var(--space-0);
}

/* The figure is drawn first and read second: `order` moves it left of its own term without moving
 * it in the DOM, so the definition list still reads "odbytych: 11" to anything that is not looking
 * at it. */
.counter {
  display: flex;
  gap: var(--space-6px);
  align-items: baseline;
}

/* THINNED 2 September 2026, and it is a decision. KANON writes 800 here; this app writes 700.
 * The owner's ask was "wszystko poza nagłówkami powinno być mniej bold", after 107 of 146 text
 * elements on the roster measured 700 or 800 and weight had stopped meaning anything. The record
 * is this slice's `AGENTS.md`; do not put it back. */
.counterValue {
  order: -1;
  min-inline-size: var(--w-ring-counter);
  margin: var(--space-0);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  text-align: right;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.counterName {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-lab);
  text-transform: uppercase;
  color: var(--text-3);
}

/* ── the day grid, its dates and its legend ──────────────────────────────────
 *
 * Owner, 1 September 2026: the old strip was a row of squares carrying their state in a fill alone,
 * with no legend and no dates — "tego diagramu nie rozumiem". Three things repair it, and none of
 * them is a colour: each state is a different DRAWING, every seventh square prints its date, and
 * the legend names all three in the roster preview's own words.
 *
 * The drawings and the tokens are `ClientPreviewPanel.module.css`'s, deliberately: a filled block
 * for a day trained, a filled block in the danger ink for one missed, a dashed outline for a rest
 * day. The two surfaces in this app that draw a training day now draw it the same way, and the
 * shapes stay apart for somebody who cannot separate the two fills. */

.attGrid {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.attCells {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: var(--space-1);
  padding: var(--space-0);
  margin: var(--space-0);
  list-style: none;
}

.attCell {
  position: relative;
  display: block;
  height: var(--h-sess-cell);
  border-radius: var(--radius-4px);
}

.attCellDone {
  background: var(--text);
  opacity: var(--o-affordance-rest);
}

/* An OUTLINE, not a fill, and the same drawing `shared/ui/TrainingWeekStrip` gives a missed day —
 * the owner chose the outline over the canon's fill on 1 September 2026, having been shown both.
 * `--danger` is the canon's own colour; the hairline is what the strip's other outlines wear. */
.attCellMissed {
  border: var(--border-hairline) solid var(--danger);
}

/* Nothing was asked on this day. Dashed rather than empty: an empty square and a missing square
 * look the same, and only one of them is a fact.
 *
 * `--border`, which is the canon's and is what `shared/ui/TrainingWeekStrip` — the same drawing on
 * the summary column and the roster panel — already wears. It stood at `--border-3` on a measured
 * departure: composited against `--bg-card`, `--border` gives 1.18:1 in Flame and 1.22:1 in Flame
 * Dark, so a third of the legend named a square that is invisible rather than faint. The owner
 * withdrew that argument on 1 September 2026; the measurement is in the round's report. */
.attCellFree {
  border: var(--border-hairline) dashed var(--border);
}

/* The date under an anchor cell. Out of flow, so a labelled square is exactly as tall as an
 * unlabelled one and the strip does not step. */

/* THINNED 2 September 2026, and it is a decision. KANON writes 700 here; this app writes 600.
 * The owner's ask was "wszystko poza nagłówkami powinno być mniej bold", after 107 of 146 text
 * elements on the roster measured 700 or 800 and weight had stopped meaning anything. The record
 * is this slice's `AGENTS.md`; do not put it back. */
.attCellDate {
  position: absolute;
  inset-block-start: calc(var(--h-sess-cell) + var(--space-1));
  inset-inline-start: var(--space-0);
  font-size: var(--fs-micro-label);
  font-style: normal;
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.attLegend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px) var(--space-14px);
  align-items: center;
  padding: var(--space-0);
  margin: var(--space-5) var(--space-0) var(--space-0);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  list-style: none;
}

.attLegendItem {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
}

/* A swatch is one cell, shrunk. Same class, same drawing — a legend that redraws its marks is a
 * legend that can disagree with the thing it explains.
 *
 * The canon's 10×12, which is also what `shared/ui/TrainingWeekStrip`'s legend draws, so the two
 * legends on this screen carry the same key.
 *
 * These were `var(--space-12px)`, a token NOTHING DEFINES. An unresolvable `var()` with no fallback
 * falls back to the property's initial value, so both dimensions became `auto` and the swatch was a
 * 0×0 empty span: the legend rendered as a row of bare words with a two-pixel smudge where the rest
 * day's dashed border met itself. Owner, 1 September 2026: "brakuje ci legendy (są same napisy)."
 * Stylelint cannot catch this — the value allowlist is keyed on real property names and a `var()`
 * is opaque to it — so the only defence is that a token reference is checked against the token
 * file. Nine more references like it exist elsewhere in this app and are reported, not fixed here. */
.attSwatch {
  flex: none;
  width: 10px;
  height: 12px;
}

.attLegendNote {
  margin-inline-start: auto;
  font-weight: var(--fw-medium);
  color: var(--text-3);
}

/* ── the charts ────────────────────────────────────────────────────────────── */

.chartFigure {
  flex: none;
  font-size: var(--fs-session);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--text);
  letter-spacing: var(--ls-section);
  font-variant-numeric: tabular-nums;
}

/* THINNED 2 September 2026, and it is a decision. KANON writes 700 here; this app writes 600.
 * The owner's ask was "wszystko poza nagłówkami powinno być mniej bold", after 107 of 146 text
 * elements on the roster measured 700 or 800 and weight had stopped meaning anything. The record
 * is this slice's `AGENTS.md`; do not put it back. */
.chartDelta {
  flex: none;
  font-size: var(--fs-micro);
  font-style: normal;
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.better {
  color: var(--good-ink);
}

.worse {
  color: var(--warn-ink);
}

/* A pending chart reserves its calibrated height from the FIRST frame. `series.empty` is true both
 * while a read is in flight and when it returns nothing, and those want opposite things: a
 * one-line notice where a chart is about to be makes the card too short and everything under it
 * jumps when the response lands. Not a skeleton — nothing shimmers and nothing is faked.
 *
 * `place-items` because the box now also holds the EMPTY state's sentence: the notice used to
 * REPLACE the plot, which cost the card the plot's whole height for every client with nothing in
 * the window. It is centred rather than pinned to the top so a one-line answer reads as the
 * chart's answer rather than as a caption above a hole. */
.chartReserve {
  display: grid;
  place-items: center;
  height: var(--h-chart-plot);
}

.chartReserveBody {
  display: grid;
  place-items: center;
  height: var(--h-chart-plot-body);
}

/* ── Trendy: muscle volume ─────────────────────────────────────────────────── */

/* ── Two more reserves, same rule as `.chartReserve` above ────────────────────────────────────
 * A card whose only content is one sentence — because the read is open, or because it answered
 * with nothing — holds the height its loaded self will have, so the cards below it do not travel
 * when the answer lands. They are applied to the SHORT states only, so a loaded card is still
 * sized by its content, and they are `min-height` so a card with more to say still grows.
 *
 * Both are CENTRED, for the reason `.chartReserve` above is: their short state is a single
 * sentence, and pinned to the top of a 147px box it left 98.75px of dead white under it and read
 * as a card that had failed to render. Neither has a head to hold at the top — their eyebrow is
 * the section heading outside the card — so centring the one child is the whole change. */
.reserveAttendance {
  justify-content: center;
  min-height: var(--h-trends-attendance);
}

.reserveVolume {
  min-height: var(--h-trends-volume);
}

/* A chart card's head holds the reading's line box before there is a reading in it. See the note at
 * the call site: the eyebrow alone is 12px and the figure beside it is 19. */
.chartHead {
  min-height: var(--h-chart-head);
}

/* ── The weekly-average / every-reading switch ────────────────────────────────────────────────
 * A two-option segmented control, in the vocabulary this app already speaks for one: the plan
 * progression drawer's family choice draws the unselected option as a bare outline and the selected
 * one as a filled, more strongly edged chip, with the ink stepping up at the same time. The fills
 * are INVERTED against that control's, because the ground is: those sit on a drawer, these sit on a
 * card, so the selected one takes the inset fill rather than the card fill.
 *
 * It stands under the plot it governs and is drawn in every state — see the call site.
 *
 * ── Three channels, because one of them is nearly nothing ────────────────────────────────────
 * Measured on the production build against `--bg-card`, composited: the selected chip's FILL is
 * 1.05:1 in Flame and 1.04:1 in Flame Dark, which is a surface step and not a mark. So the
 * selection is carried by the EDGE — `--border-3` at 3.32:1 / 3.60:1 against the card and 2.86 /
 * 2.82 against the unselected option's `--border`, which is itself 1.16 / 1.28 and invisible as a
 * discriminator — and by the INK: `--text` at 17.93:1 / 17.36:1 on the chip against `--text-3`'s
 * 3.01 / 4.02 on the card. `aria-checked` carries it for assistive technology, which is Base UI's.
 *
 * `align-self: flex-start` so the pair is only as wide as its two words; full-bleed it read as a
 * tab bar for the card rather than as a switch inside it. */
.scaleGroup {
  display: inline-flex;
  gap: var(--space-6px);
  align-self: flex-start;
}

/* Base UI's `Radio.Root` renders a `<span role="radio">` with a visually hidden input beside it, so
 * every visual byte below is ours — including `cursor`, which a span has no default for.
 *
 * The border is split into three declarations rather than written as a shorthand: the Stylelint
 * value allowlist requires a TOKEN colour inside a `border` shorthand, correctly, since a literal
 * could ride in the same way. */
.scaleOption {
  display: inline-flex;
  align-items: center;
  height: var(--h-detail-pill);
  padding: 0 var(--space-10px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: var(--border);
  border-radius: var(--r-pill);
  transition-duration: var(--dur-fast);
  transition-property: color, background-color, border-color;
  transition-timing-function: var(--ease);
}

.scaleOption[data-checked] {
  color: var(--text);
  background: var(--bg-inset);
  border-color: var(--border-3);
}

.scaleOption:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.scaleOption:hover:not([data-checked]) {
  color: var(--text-2);
  border-color: var(--border-2);
}

.volumeRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6);
  align-items: flex-start;
}

.figureColumn {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-10px);
  width: var(--w-volume-figure);
}

.figureRow {
  display: flex;
  gap: var(--space-10px);
  justify-content: center;
}

.figureBox {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-6px);
  align-items: center;
  min-width: 0;
  margin: var(--space-0);
}

.figureCaption {
  font-size: var(--fs-badge);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  letter-spacing: var(--ls-rail);
  text-transform: uppercase;
}

.zoneLegendList {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  padding: var(--space-0);
  margin: var(--space-0);
  list-style: none;
}

/* The legend WRAPS. The canon's entries are two words ("za mało"); the copied locale spells the
 * whole rule out ("1–9 — za mało (poniżej MEV, progu wzrostu)"), which is 287px of nowrap text in a
 * 268px column — measured, and it ran past the figure column's edge on every render. The swatch
 * stays on the first line with `align-items: baseline` doing the work a nowrap row used to. */
.zoneLegendItem {
  display: flex;
  gap: var(--space-6px);
  align-items: baseline;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint);
  color: var(--text-2);
  text-wrap: pretty;
}

/* THINNED 2 September 2026, and it is a decision. KANON writes 700 here; this app writes 600.
 * The owner's ask was "wszystko poza nagłówkami powinno być mniej bold", after 107 of 146 text
 * elements on the roster measured 700 or 800 and weight had stopped meaning anything. The record
 * is this slice's `AGENTS.md`; do not put it back. */
.zoneRange {
  font-weight: var(--fw-semibold);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

/* The canon draws these four swatches as flat fills on the card, with no ring. A ring was added on
 * a measured argument — against `--bg-chip` the ramp's amber measured 2.44:1, green 2.80, red 2.72
 * and the zero zone 1.07:1 — and withdrawn on 1 September 2026 with the rest of the measured
 * departures. The numbers are in the round's report; the zone's own word is beside every swatch,
 * so nothing here is carried by the fill alone. */
.zoneSwatch {
  display: block;
  flex: none;
  align-self: center;
  width: var(--space-10px);
  height: var(--space-10px);
  border-radius: var(--radius-hair);
}

.zoneSwatchNone {
  background: var(--bg-sunken);
}

.noneFill {
  background: var(--bg-sunken);
}

.lowFill {
  background: var(--warn);
}

.optFill {
  background: var(--good);
}

/* "Za dużo" is the ACCENT, not the danger red — the canon's own `ZC` table maps the over-training
 * zone to ember. It is the one accent spend on this tab and it marks a real state. */
.highFill {
  background: var(--accent);
}

.clusters {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3);
  min-width: var(--w-cluster-min);
}

.cluster {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  padding-bottom: var(--space-1);
}

.clusterHead {
  margin: var(--space-0);
  font-size: var(--fs-badge);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  letter-spacing: var(--ls-lab);
  text-transform: uppercase;
}

.muscleList {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  padding: var(--space-0);
  margin: var(--space-0);
  list-style: none;
}

.muscleRow {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
}

.muscleName {
  flex: none;
  width: var(--w-muscle-name);
  overflow: hidden;
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.muscleTrack {
  position: relative;
  display: block;
  flex: 1;
  min-width: var(--w-muscle-track-min);
  height: var(--h-volume-track);
  overflow: hidden;
  background: var(--bg-sunken);
  border-radius: var(--radius-4px);
}

.muscleFill {
  display: block;
  height: 100%;
  border-radius: var(--radius-4px);
}

/* The two threshold marks the canon rules the track with. They are what make a bar readable as a
 * POSITION between bands rather than as a fraction of an arbitrary maximum. */
.muscleTick {
  position: absolute;
  inset-block: var(--space-0);
  width: var(--border-hairline);
  background: var(--border-2);
}

/* THINNED 2 September 2026, and it is a decision. KANON writes 700 here; this app writes 600.
 * The owner's ask was "wszystko poza nagłówkami powinno być mniej bold", after 107 of 146 text
 * elements on the roster measured 700 or 800 and weight had stopped meaning anything. The record
 * is this slice's `AGENTS.md`; do not put it back. */
.muscleSets {
  flex: none;
  width: var(--w-muscle-sets);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  text-align: end;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* ── Pomiary ─────────────────────────────────────────────────────────────────
 *
 * KANON `pomiary-klienta-2026-09-18`, direction D. One stage card and one comparison table.
 *
 * The plot's overlays and its two handles are `TwoDates.module.css`, because they live inside the
 * chart's own coordinate space and nothing else on this tab does. The drawer is
 * `MeasurementDrawer.module.css`. What is here is the two cards.
 *
 * The accent is spent FOUR times on this tab, all of them on the selection the trainer made: the
 * active preset's dot, the two handles, and the stretch of line between them. Nothing else on the
 * screen is ember — the bars are neutral ink unless a fall is the goal, in which case they are
 * `--good-ink`, which is the shipped tab's own rule and the canon's. */

/* The two cards' own padding. `.card` is the three tabs' shared box and stays where it is; the
 * canon gives these two 20px 22px 14px and 18px 22px, which is a taller card with a tighter foot
 * under the plot. Worn as a second class, so nothing else on Trendy or Rekordy moves. */
.stageCard {
  gap: var(--space-0);
  padding: var(--space-20px) var(--space-22px) var(--space-14px);
}

.tableCard {
  gap: var(--space-14px);
  padding: var(--space-5) var(--space-22px);
}

.stageHead {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: var(--space-5);
}

/* The reading and the sentence, in a box that knows its height before either arrives — so the
 * 400px plot under them never travels when the read lands. */
.stageSay {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  justify-content: flex-start;
  min-width: 0;
  min-height: var(--h-two-dates-head);
}

.nowLine {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  margin: var(--space-0);
  font-size: var(--fs-two-dates-now);
  line-height: var(--lh-auto);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.nowLabel {
  font-weight: var(--fw-medium);
}

.nowValue {
  font-size: var(--fs-two-dates-sentence);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  letter-spacing: var(--ls-heading);
}

.nowDate {
  font-size: var(--fs-label);
  color: var(--text-2);
}

/* The one sentence the tab is about. It is the loudest thing on the screen because it is the
 * answer; everything else is the apparatus for asking a different version of the question. */
.sentence {
  /* The canon's own 34ch. It was 44 while the reserve below was being measured; at 34 the sentence
   * wraps to two lines more often, which the two-line reserve already holds — so the canon's
   * measure costs nothing and is what the specimen draws. */
  max-width: 34ch;

  /* Two lines, always. Whether this sentence wraps is a function of the two DATES in it, so a
   * one-line reserve made the plot under it move when a preset changed them. */
  min-height: var(--h-two-dates-sentence);
  margin: var(--space-0);
  font-size: var(--fs-two-dates-sentence);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-tight);
  color: var(--text);
  letter-spacing: var(--ls-heading);
  font-variant-numeric: tabular-nums;
}

/* "do" and "w 28 tyg." — scaffolding around the two dates and the figure, and quieter than them.
 *
 * `--text-3` STAYS here, alone among the Pomiary rules, and it is measured rather than assumed: the
 * dimming is the canon's own look and the owner approved it, and at 22px/800 this is LARGE text, so
 * the bar is 3:1 (WCAG 1.4.3) and not 4.5. On `--bg-card` it measures **3.01:1 in Flame** and
 * **4.02:1 in Flame Dark**. Everything else the 18.09 round put on `--text-3` is note-sized or
 * smaller and has moved to `--text-2`, per this file's own standing deviation above. */
.sentenceWord {
  font-style: normal;
  color: var(--text-3);
}

.sentenceGood {
  color: var(--good-ink);
}

/* What stands where the sentence would be when there is nothing to compare. Same box, quieter
 * voice: this is an answer, not a wait. */
.sentenceQuiet {
  margin: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.stageControls {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-10px);
  align-items: flex-end;
}

/* The chip row and, under it, the reason any refused chip carries. One column so the caption
 * cannot widen the row it explains. */
.presetsBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  align-items: flex-end;
  min-width: 0;
}

.presets {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px);
  justify-content: flex-end;
}

/* Why a preset or a toggle is refused, in words — VISIBLE, so a keyboard reaches it through
 * `aria-describedby` and a mouse reaches it by reading. It was a `title`, which is neither, and it
 * was one sentence for four different refusals.
 *
 * `.presetReason` holds its LINE whether or not it has anything to say: it is drawn unconditionally
 * with an empty string, so the stage head is the same height in every state and a refusal landing
 * with the answer changes ink and not geometry. Measured before the reserve: 24px of movement on
 * the plot, the table card and its first row, on a cold entry at 1200ms of latency. The stage head
 * stands 8px taller than the canon's 94px reserve for it, which is the whole cost. */
.presetReason,
.toggleReason {
  font-size: var(--fs-micro-label);
  font-style: normal;
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* 14px covers one 9.5px line at `--lh-normal` (12.35px) with nothing left over to grow by. */
.presetReason {
  display: block;
  min-height: var(--space-14px);
}

/* The canon's `toggle`: an outlined pill with a ring that fills when it is on. The ACTIVE preset is
 * the only one that carries ember, and it carries it on the ring rather than as a fill — a filled
 * ember pill in a row of four would read as a primary action.
 *
 * The ring's own ink is a TOKEN per state rather than `currentcolor`: Stylelint's value allowlist
 * refuses `currentcolor` on a colour property, correctly, since it is the one value that can carry
 * a literal in from anywhere up the tree. */
.preset,
.toggle {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  height: var(--h-two-dates-pill);
  padding: 0 var(--space-10px) 0 var(--space-2);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: var(--border-2);
  border-radius: var(--r-pill);
  transition-duration: var(--dur-fast);
  transition-property: color, border-color;
  transition-timing-function: var(--ease);
}

.presetDot,
.toggleDot {
  width: var(--space-2);
  height: var(--space-2);
  border-width: var(--border-focus);
  border-style: solid;
  border-color: var(--border-3);
  border-radius: var(--r-pill);
}

.preset:hover,
.toggle:hover {
  color: var(--text-2);
  border-color: var(--border-3);
}

.preset:focus-visible,
.toggle:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* A refused pill, and the refusal is carried by the BORDER and the dot rather than by fading the
 * whole control. `--text-3` under `opacity: .55` composited to about 1.8:1 on the card — a label
 * nobody can read is not an explanation of anything, and the explanation is the reason a refused
 * control stays on screen at all. The label sits at `--text-2`, the same ink every other caption on
 * this tab wears, and what says "refused" is the hairline border and the hollow dot. */
.presetOff,
.toggleOff {
  color: var(--text-2);
  cursor: not-allowed;
  border-style: dashed;
  border-color: var(--border);
}

.presetOff .presetDot,
.toggleOff .toggleDot {
  border-color: var(--border);
}

.presetOff:hover,
.toggleOff:hover {
  color: var(--text-2);
  border-color: var(--border);
}

.presetOn {
  color: var(--text);
  border-color: var(--accent);
}

.presetOn .presetDot {
  background: var(--accent);
  border-color: var(--accent);
}

.toggleOn {
  color: var(--text);
  border-color: var(--border-3);
}

.toggleOn .toggleDot {
  background: var(--text);
  border-color: var(--text);
}

/* The canon's own „+ Pomiar": the ghost treatment at the specimen's geometry — 34px tall, a 10px
 * radius, 12.5px at 600, and a 7px gap between the drawn plus and the word. It is NOT `.ghost`
 * above, which four other surfaces on this slice wear and which must not move for this one card. */
.measureButton {
  gap: var(--space-7px);
  height: var(--h-detail-btn);
  padding: 0 var(--space-13px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  background: transparent;
  border-radius: var(--radius-sm);
}

/* „Linia:", the switch and the „Pomiar" button stand on ONE line, vertically centred. The canon
 * draws exactly that. With eleven options the pill may not fit a narrow card, so it is the one box
 * here allowed to shrink, and it scrolls sideways rather than wrapping onto a second row. */
.lineRow {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  justify-content: flex-end;
}

.lineLabel {
  flex: none;
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

/* ── the segmented pill ──────────────────────────────────────────────────────
 *
 * ONE control, not six: a chip-coloured track with the chosen option lifted onto the card's own
 * fill inside a hairline ring. That lift is the whole discriminator, so the unselected options
 * carry NO border at all — which is what separates this from `.scaleOption` above, where two
 * options each wear their own outline. Reusing that control here produced eleven bordered chips
 * wrapping onto two rows, and it read as a list rather than as a switch.
 *
 * The ring is a `box-shadow` rather than a border because a border on the selected option alone
 * would change its box and shuffle the five beside it on every switch. Neutral ink, never a glow. */
.seg {
  display: inline-flex;
  flex: 0 1 auto;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
  gap: var(--space-2px);
  align-items: center;
  padding: var(--space-3px);
  background: var(--bg-chip);
  border-radius: var(--r-pill);
}

/* Base UI's `Radio.Root` renders a `<span role="radio">`, so every visual byte here is ours —
 * including `cursor`, which a span has no default for. */
.segOption {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: var(--h-two-dates-pill);
  padding: 0 var(--space-11px);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--r-pill);
  transition-duration: var(--dur-fast);
  transition-property: color, background-color;
  transition-timing-function: var(--ease);
}

.segOption[data-checked] {
  color: var(--text);
  background: var(--bg-card);
  box-shadow: var(--ring-seg);
}

.segOption:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.segOption:hover:not([data-checked]) {
  color: var(--text);
}

/* The plot's own box, and the box a wait, a failure and an empty answer all stand in. Centred, so a
 * one-line answer reads as the chart's answer rather than as a caption above a hole. */
.plotReserve {
  display: grid;
  place-items: center;
  min-height: var(--h-chart-plot-two-dates);
}

/* ── Rekordy ───────────────────────────────────────────────────────────────── */

.liftFigureRow {
  display: flex;
  gap: var(--space-5px);
  align-items: baseline;
  margin: var(--space-0);
}

.liftFigure {
  font-size: var(--fs-detail-b3);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--text);
  letter-spacing: var(--ls-empty);
  font-variant-numeric: tabular-nums;
}

.liftUnit {
  font-size: var(--fs-label);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
}

/* The date on a Big-3 card's HEAD. The canon's `b3Date` — 11 / 600 / `--text-3`, which is the same
 * register `b3Est` wears beside the figure below it. It was drawing as `.aside`, a step lighter,
 * which is a section caption's weight and not a dated fact's. */
.liftWhen {
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
}

.liftAside {
  flex: 1;
  overflow: hidden;
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  text-align: end;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prList {
  display: flex;
  flex-direction: column;
  padding: var(--space-0);
  margin: var(--space-0);
  list-style: none;
}

.prRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-10px);
  align-items: center;
  min-height: var(--h-pr-row);
}

.prRow + .prRow {
  border-top: var(--border-hairline) solid var(--border);
}

/* v3's mark: a 30px square on the full lime with a dark glyph on it.
 *
 * The fill is the raw `--lime` in BOTH themes, which is the owner's 31.08 decision that a record
 * is lime. On a white card that fill measures 1.35:1 as a shape, so it carries a hairline ring —
 * the same mitigation the volume zones already use — and the glyph on it measures 13.94:1, which
 * is what actually has to be read. */
.prMark {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--sz-pr-mark);
  height: var(--sz-pr-mark);
  background: var(--lime);
  border: var(--border-hairline) solid var(--border-3);
  border-radius: var(--radius-8px);
}

.prGlyph {
  display: block;
  width: var(--sz-pr-glyph);
  height: var(--sz-pr-glyph);
  color: var(--on-pr);
}

.prKind {
  flex: none;
  width: var(--w-pr-kind);
  font-size: var(--fs-badge);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  letter-spacing: var(--ls-cat);
  text-transform: uppercase;
  white-space: nowrap;
}

.prName {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prValue {
  flex: none;
  font-size: var(--fs-exercise);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--text);
  letter-spacing: var(--ls-title);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* The gain is the record's own colour — lime, per theme. It sits beside the value it belongs to,
 * and the axis it belongs to is named in words two columns left, so the hue is never alone. */

/* THINNED 2 September 2026, and it is a decision. KANON writes 800 here; this app writes 700.
 * ONE step, not the two the rest of the pass took, and deliberately so: `--lime-ink` measures
 * 3.38:1 in Flame light, so the weight is carrying part of what the colour cannot. Do not take it
 * to 600 "for hierarchy". The record is `src/pages/client-detail/AGENTS.md`. */
.prGain {
  flex: none;
  width: var(--w-pr-delta);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--lime-ink);
  text-align: end;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.prWhen {
  flex: none;
  width: var(--w-pr-when);
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
  text-align: end;
  white-space: nowrap;
}

/* A set that broke two or three records at once. It wraps onto its own line rather than squeezing
 * the row's fixed columns, because the second axis is a real fact and dropping it would print only
 * the largest of three. */
.prExtra {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  width: 100%;
  padding-inline-start: var(--space-10);
  padding-bottom: var(--space-2);
}

.prExtraItem {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: baseline;
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* The table scrolls sideways rather than clipping. Four columns fit at every width this app
 * supports; the scroller is the fallback, not the normal case. */
.tableScroll {
  min-width: 0;
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.th {
  padding: var(--space-0) var(--space-0) var(--space-6px);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  text-align: start;
  letter-spacing: var(--ls-column-clients);
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: var(--border-hairline) solid var(--border);
}

.thNumeric,
.thWhen {
  text-align: end;
}

/* The header cells carry the same widths as the body cells. Without them the numeric columns size
 * to their own captions and the table's four tracks disagree with the canon's `84px 84px 52px`. */
.thNumeric {
  width: var(--w-record-value);
}

.thWhen {
  width: var(--w-record-when);
}

.tdName,
.tdNumeric,
.tdWhen {
  height: var(--h-ex-row);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  border-bottom: var(--border-hairline) solid var(--border);
}

.tdName {
  overflow: hidden;
  font-size: var(--fs-note);
  color: var(--text);
  text-align: start;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tdNumeric {
  width: var(--w-record-value);
  font-size: var(--fs-note);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  text-align: end;
  letter-spacing: var(--ls-title);
  font-variant-numeric: tabular-nums;
}

.tdE1rm {
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.tdWhen {
  width: var(--w-record-when);
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  color: var(--text-3);
  text-align: end;
  white-space: nowrap;
}

/* The last row's rule is the card's own edge, so it is dropped. */
.table tbody tr:last-child .tdName,
.table tbody tr:last-child .tdNumeric,
.table tbody tr:last-child .tdWhen {
  border-bottom: none;
}

/* ── The tabs' skeletons ───────────────────────────────────────────────────────
 *
 * The treatment — the fill, the radius and the slow neutral sheen — is `shared/ui/skeleton`'s.
 * Everything here is geometry, which is the half a shared module deliberately does not own.
 *
 * Every one of these stands INSIDE a box its card already reserves, so drawing it moves nothing:
 * the chart bars sit in `.chartReserve` / `.chartReserveBody`, the ring row in
 * `.reserveAttendance`, the volume shape in `.reserveVolume`. That is what lets the grace window
 * hold the bars back without the card changing height when they arrive. */

/* A plot's shape is a LINE, so its skeleton is one band across the box the chart will occupy. */
.skelPlot {
  width: 100%;
  height: var(--space-10);
}

/* Trendy's frekwencja card: the ring and the two lines beside it. */
.skelRingRow {
  display: flex;
  gap: var(--space-22px);
  align-items: center;
}

.skelRing {
  flex: none;
  width: var(--sz-goal-ring);
  height: var(--sz-goal-ring);
}

.skelRingText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.skelLead {
  width: 42%;
  height: var(--fs-body);
}

.skelLine {
  width: 66%;
  height: var(--fs-micro);
}

/* Objętość mięśniowa: the silhouette pair beside the per-muscle rows. Deliberately UNDER the
 * card's 409px reserve — a skeleton taller than the reserve makes the card shrink when the answer
 * lands, which is the hole this whole round is closing. */
.skelVolume {
  display: flex;
  gap: var(--space-6);
  align-items: flex-start;
}

.skelSilhouette {
  flex: none;
  width: var(--w-volume-figure);
  height: 300px;
  border-radius: var(--radius-sm);
}

.skelVolumeRows {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}

.skelVolumeRow {
  width: 100%;
  height: var(--space-8);
}

/* Pomiary: the reading and the sentence. Each bar stands where its own content will, and each fits
 * inside the reserve it is drawn in. */
.skelNow {
  width: 180px;
  height: var(--fs-two-dates-sentence);
}

.skelSentence {
  width: 62%;
  max-width: 420px;
  height: var(--fs-two-dates-sentence);
}

/* A row of the per-exercise TABLE's skeleton. Its own floor rather than the feed's, because a
 * `<tr>` of plain type is shorter than a 46px feed row and reserving the feed's made the card
 * 11.5px taller than the table that replaced it. */
.skelTableRow {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  min-height: var(--h-history-row);
}

/* Rekordy: a feed row and a table row, both on `.prRow`'s own 46px floor. */
.skelPrMark {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-sm);
}

.skelPrKind {
  flex: none;
  width: 58px;
  height: var(--fs-caption);
}

.skelPrName {
  flex: 1;
  min-width: 0;
  max-width: 190px;
  height: var(--fs-exercise);
}

.skelPrValue {
  flex: none;
  width: 64px;
  height: var(--fs-body);
}

.skelPrWhen {
  flex: none;
  width: 46px;
  height: var(--fs-caption);
}

/* ── The two "wait or empty" boxes ───────────────────────────────────────────
 *
 * A card whose skeleton and whose empty answer are BOTH shorter than its loaded self needs one box
 * that holds them, or the skeleton's own arrival becomes the jump it was added to prevent.
 * Measured before these existed: the records feed grew 152px and the snapshot 71px in the frame
 * the bars appeared, and the history card then SHRANK 49.75px when the empty answer replaced them.
 *
 * Their heights are derived rather than measured: three rows of the list each one is standing in
 * for, off that list's own row token. So the skeleton fills the box exactly, and a change to the
 * row height carries the reserve with it. */
.rowsWait3 {
  display: grid;
  align-content: center;
  min-height: calc(var(--h-pr-row) * 3);
}

/* The per-exercise TABLE's rows are shorter than the feed's — a plain `<tr>` of type against a
 * 46px feed row — so it reserves the history list's row instead. Measured after: three feed rows
 * left the card 11.5px taller than the loaded table, which is a hole rather than a growth. */
.tableWait3 {
  display: grid;
  align-content: center;
  min-height: calc(var(--h-history-row) * 3);
}
